import { defineStore } from 'pinia'
import { ref, computed, onMounted, onUnmounted } from 'vue'
import { useScreenSafeArea } from '@vueuse/core'
import { useDisplay } from 'vuetify'

export const useDeviceStore = defineStore('device', () => {
	const {
		top: screenSafeAreaTop,
		right: screenSafeAreaRight,
		bottom: screenSafeAreaBottom,
		left: screenSafeAreaLeft,
	} = useScreenSafeArea()
	const screenSafeAreaTopValue = computed(() => (parseFloat(screenSafeAreaTop.value) ?? 0))
	const screenSafeAreaRightValue = computed(() => (parseFloat(screenSafeAreaRight.value) ?? 0))
	const screenSafeAreaBottomValue = computed(() => (parseFloat(screenSafeAreaBottom.value) ?? 0))
	const screenSafeAreaLeftValue = computed(() => (parseFloat(screenSafeAreaLeft.value) ?? 0))

	// 1. ИНТЕГРАЦИЯ С VUETIFY
	// Получаем мощный сервис `display` от Vuetify
	const {
		xs,
		sm,
		md,
		lg,
		xl,
		xxl,
		width,
		height,
		mobile,
		platform,
		name: currentBreakpoint,
	} = useDisplay()

	// 2. КАСТОМНЫЕ РЕАКТИВНЫЕ ПЕРЕМЕННЫЕ
	// Этих данных нет в Vuetify, поэтому определяем их сами

	const orientation = ref(getOrientation())
	const isTouchSupported = ref(false) // Определим в onMounted, т.к. нужен `window`
	/**
	 * @description Текущая ширина видимой области (уменьшается при появлении клавиатуры).
	 */
	const visualViewportWidth = ref(0)
	/**
	 * @description Текущая высота видимой области (уменьшается при появлении клавиатуры).
	 */
	const visualViewportHeight = ref(0)
	/**
	 * @description Текущий уровень масштабирования (зума) страницы. 1.0 = 100%.
	 */
	const visualViewportScale = ref(1)

	// 3. СТАТИЧЕСКИЕ ДАННЫЕ (из переменных окружения)
	const isProductionMode = computed(() => {
		return import.meta.env.VITE_APP_ENV_MODE === 'prod'
	})
	const isStageMode = computed(() => {
		return import.meta.env.VITE_APP_ENV_MODE === 'stage'
	})
	const isProductionOrStageMode = computed(() => {
		return ['prod', 'stage'].includes(import.meta.env.VITE_APP_ENV_MODE)
	})

	const project = import.meta.env.VITE_APP_PROJECT

	const isOntrackProject = project === 'ontrack'

	const isScoutingProject = project === 'scouting'

	// 4. ВЫЧИСЛЯЕМЫЕ СВОЙСТВА (COMPUTED)
	// Комбинируем данные из Vuetify и наши кастомные

	/**
	 * Запущено ли приложение в режиме PWA (отдельное окно)
	 */
	const isPwa = computed(() => {
		if (typeof window === 'undefined') return false
		return window.matchMedia('(display-mode: standalone)').matches
	})

	// --- Базовые определения устройств (атомарные) ---
	// Сначала создаем независимые, точные флаги

	/**
	 * Определяет, является ли устройство iPhone.
	 * Самое простое определение на основе флага от Vuetify.
	 */
	const isIphone = computed(() => {
		return platform.value.ios
	})

	/**
	 * Определяет, является ли устройство iPad.
	 * Ключевой признак: iPadOS 13+ притворяется десктопным Mac Safari,
	 * но, в отличие от большинства Mac, имеет сенсорный экран.
	 */
	const isIpad = computed(() => {
		if (typeof navigator === 'undefined') return false
		return platform.value.mac && navigator.maxTouchPoints > 0 && !isIphone.value
	})

	/**
	 * Определяет, является ли устройство телефоном на Android.
	 * Признак: платформа Android и ширина экрана как у телефона.
	 */
	const isAndroidPhone = computed(() => {
		return platform.value.android && mobile.value
	})

	/**
	 * Определяет, является ли устройство планшетом на Android.
	 * Признак: платформа Android и ширина экрана больше, чем у телефона.
	 */
	const isAndroidTablet = computed(() => {
		return platform.value.android && !mobile.value
	})

	// --- Обобщенные определения (композитные) ---
	// Теперь собираем из "кирпичиков" нужные нам высокоуровневые флаги

	/**
	 * Является ли устройство телефоном (любой ОС)
	 */
	const isMobilePhone = computed(() => {
		return isIphone.value || isAndroidPhone.value
	})

	/**
	 * Является ли устройство планшетом (любой ОС)
	 */
	const isTablet = computed(() => {
		return isIpad.value || isAndroidTablet.value
	})

	/**
	 * Обобщенный тип устройства
	 * @returns {'mobile' | 'tablet' | 'desktop'}
	 */
	const deviceType = computed(() => {
		if (isMobilePhone.value) return 'mobile'
		if (isTablet.value) return 'tablet'
		return 'desktop'
	})

	// 5. ВСПОМОГАТЕЛЬНЫЕ ФУНКЦИИ И СЛУШАТЕЛИ СОБЫТИЙ

	/**
	 * Получает текущую ориентацию экрана
	 * @returns {'portrait' | 'landscape'}
	 */
	function getOrientation() {
		if (typeof window === 'undefined') return 'portrait'
		return window.matchMedia('(orientation: portrait)').matches ? 'portrait' : 'landscape'
	}

	/**
	 * Обработчик изменения ориентации
	 */
	function handleOrientationChange() {
		orientation.value = getOrientation()
	}

	/**
	 * @description Обновляет реактивные переменные значениями из Visual Viewport API.
	 */
	function handleViewportChange() {
		// Проверяем, что API доступно (важно для SSR и старых браузеров)
		if (!window.visualViewport) return

		visualViewportWidth.value = window.visualViewport.width
		visualViewportHeight.value = window.visualViewport.height
		visualViewportScale.value = window.visualViewport.scale
	}

	// 6. LIFECYCLE HOOKS (Управление слушателями)
	// Используем onMounted, чтобы безопасно работать с `window` и `document`
	onMounted(() => {
		isTouchSupported.value = 'ontouchstart' in window || navigator.maxTouchPoints > 0

		// Добавляем слушатель изменения ориентации
		window.addEventListener('orientationchange', handleOrientationChange)

		// Проверяем, что мы на клиенте и API поддерживается
		if (typeof window !== 'undefined' && window.visualViewport) {
			// Устанавливаем начальные значения
			handleViewportChange()
			// Подписываемся на события изменения (зум, появление клавиатуры)
			window.visualViewport.addEventListener('resize', handleViewportChange)
		}
	})

	onUnmounted(() => {
		// Обязательно убираем слушатель, чтобы избежать утечек памяти
		window.removeEventListener('orientationchange', handleOrientationChange)

		// --- ОБЯЗАТЕЛЬНО ОТПИСЫВАЕМСЯ ОТ СОБЫТИЙ ---
		if (typeof window !== 'undefined' && window.visualViewport) {
			window.visualViewport.removeEventListener('resize', handleViewportChange)
		}
	})

	return {
		screenSafeAreaTop,
		screenSafeAreaRight,
		screenSafeAreaBottom,
		screenSafeAreaLeft,
		screenSafeAreaTopValue,
		screenSafeAreaRightValue,
		screenSafeAreaBottomValue,
		screenSafeAreaLeftValue,

		// Данные из Vuetify
		xs,
		sm,
		md,
		lg,
		xl,
		xxl,
		width,
		height,
		mobile,
		platform,
		currentBreakpoint,

		// Наши кастомные данные
		isProductionMode,
		isStageMode,
		isProductionOrStageMode,
		project,
		isOntrackProject,
		isScoutingProject,

		orientation,
		isTouchSupported,

		visualViewportWidth,
		visualViewportHeight,
		visualViewportScale,

		// Наши вычисляемые свойства
		isPwa,
		isTablet,
		isMobilePhone,
		deviceType,

		// Можно вернуть и атомарные флаги для отладки или специфичных случаев
		isIpad,
		isIphone,
		isAndroidPhone,
		isAndroidTablet,
	}
})
